import type { Translations } from "./sl";
import { FAQ_EN } from "@/content/faqData";
import { legalEn } from "./legal-en";
import { propertyChecksEn } from "./property-checks-en";
import { supportingDocumentsEn } from "./supporting-documents";

export const en: Translations = {
  common: {
    error: "Error",
    cancel: "Cancel",
    send: "Send",
    sending: "Sending...",
    or: "Or",
    and: "and",
    aptNo: "No.",
    aptNoTitle: "Apartment number",
    toggleMenu: "Toggle menu",
    skipToContent: "Skip to content",
    language: "Language",
  },
  announcement: {
    text: "Try it free — a preview report for your address.",
    cta: "Get started",
    dismiss: "Dismiss announcement",
  },
  header: {
    features: "Features",
    howItWorks: "How it works",
    pricing: "Pricing",
    dashboard: "Dashboard",
    adminDashboard: "Admin dashboard",
    signOut: "Sign out",
    login: "Log in",
    register: "Sign up",
  },
  brand: {
    "startLabel": "Start with the property address",
    "searchCta": "Continue",
    "freeNote": "Free preview after email verification.",
    "sampleLabel": "Sample report",
    "sampleNote": "Fictional data · content preview",
    "sampleType": "Apartment · Ljubljana",
    "estimate": "Indicative value estimate",
    "uncertainty": "Estimate range · showing uncertainty",
    "sources": "Evidence behind the estimate",
    "sourceSales": "Completed sales",
    "sourceRegistry": "GURS records",
    "sourceListings": "Comparable listings",
    "checks": "Still to check",
    "checkTitle": "Land-registry status",
    "checkNote": "Verify ownership and encumbrances before signing.",
    "sourceTitle": "Give your decision a firmer foundation.",
    "sourceDescription": "Public records and market data, brought into focus. Availability depends on the property.",
    "sourceGurs": "Building, unit and land records",
    "sourceEtn": "Recorded sales and price movements",
    "sourceZk": "Ownership and encumbrances when an extract is available",
    "sourceAds": "Context from current asking prices",
    "sampleCta": "See what’s inside the report",
    "sampleDisclaimer": "Illustration using fictional data, not an analysis of this address.",
    "featuresEyebrow": "Inside the report",
    "featuresTitle": "From information to a clearer picture.",
    "featuresDescription": "See the estimate alongside its evidence, and the questions that still need answers.",
    "processTitle": "From an address to your next step.",
    "illustration": "Illustration of the difference",
    "included": "In every full report",
    "previewEyebrow": "A closer look",
    "previewTitle": "A report you can put to work.",
    "previewDescription": "Save it, share it with your partner or take it to a professional. The important facts, evidence and next steps stay together.",
    "previewPoints": [
      "A clear estimate with its uncertainty",
      "Traceable evidence and comparable sales",
      "Open risks and checks before signing"
    ],
    "previewLanguage": "The sample report is in Slovenian.",
    "featureItems": [
      {
        "title": "GURS property records",
        "description": "Areas, construction year, building and land records, and generalized value."
      },
      {
        "title": "Completed sales",
        "description": "Comparable recorded transactions nearby, their prices and dates."
      },
      {
        "title": "Location and surroundings",
        "description": "The property’s setting, accessibility and location factors."
      },
      {
        "title": "An explained estimate",
        "description": "An indicative value with an uncertainty range and the evidence behind it."
      },
      {
        "title": "Risks and next steps",
        "description": "Clearly marked outstanding checks and evidence for your price discussion."
      },
      {
        "title": "A clear PDF",
        "description": "A report to save, share or take to a professional."
      }
    ]
  },
  hero: {
    badgeGurs: "Official GURS data",
    badgeValidity: "Credits valid for 1 year",
    badgeReports: "100+ generated reports",
    eyebrow: "Property reports for Slovenia",
    titleLead: "First, the facts.",
    titleHighlight: "Then, your decision.",
    subtitle:
      "Understand the property’s price, compare completed sales and see what still needs checking. Together in one clear report.",
    tagline: "An independent view of public records and market data.",
    featureOfficialTitle: "Official data",
    featureOfficialDesc:
      "Direct access to the latest official ETN, GURS, and land registry records.",
    featureMarketTitle: "Market analysis & comparisons",
    featureMarketDesc:
      "Compare with past sales and current listings in the surrounding area.",
    featureAiTitle: "Arguments for negotiating",
    featureAiDesc:
      "A PDF showing you how to negotiate a lower price and which risks to check before you sign.",
    ctaOrder: "Order the full report",
    ctaPreview: "View sample",
  },
  propertyNextStep: {
    "eyebrow": "Selected property",
    "edit": "Edit address",
    "previewTitle": "Start with a free preview",
    "previewNote": "See GURS property facts and a findings teaser for this address. Our value estimate and full analysis unlock with a paid credit.",
    "noCard": "One preview after email verification · no card needed",
    "signedInTitle": "Continue with this property",
    "signedInNote": "Your address will be ready on the dashboard. Use your available credits there for a preview or a full report.",
    "fullTitle": "The full report for this address",
    "fullNote": "A value estimate with its range, comparable sales, risks and next steps. Coverage depends on available data.",
    "fullCta": "Full report · {{price}}",
    "packagesNote": "1 credit for this property · multi-property packages below"
  },
  addressSearch: {
    incompleteAddress: "Enter a street or place name and a house number.",
    addressLabel: "Property address",
    propertyTypeLabel: "Property type",
    house: "House",
    apartment: "Apartment",
    placeholder: "Enter address (e.g. Trubarjeva 10, Ljubljana)",
    ordering: "Ordering...",
    getReport: "Get a free report",
    hintHouse: "Enter the address of the house you're interested in for a value estimate",
    hintApartment: "Enter the address of the apartment you're interested in for a value estimate",
    hintApartmentNo: " • Add apartment number",
    toastEnterAddress: "Enter an address",
    toastEnterAddressDesc: "Please enter the property address.",
    toastEnterApt: "Enter apartment number",
    toastEnterAptDesc: "Please enter the apartment number.",
    suggestionsStatus: "{{count}} address suggestions. Use the arrow keys and press Enter to choose.",
    suggestionsLabel: "Address suggestions",
  },
  askingVsRealized: {
    eyebrow: "Asking vs realized prices",
    title: "An asking price is just the starting point.",
    askingLabel: "What the listing says",
    askingValue: "€250,000",
    askingNote: "The seller's expectation",
    realizedLabel: "What the neighbouring apartment sold for",
    realizedValue: "€218,000",
    realizedNote: "What somebody actually paid (ETN register)",
    question: "An asking price is an expectation. A sale price is a recorded payment.",
    answer:
      "To assess a price, you need comparable completed sales, their dates and the differences between properties. The report brings them together and explains what they support and where the data is limited.",
    caveat:
      "The gap between asking and realized prices is not all negotiating room — part of it comes from differences between the compared properties and from listings covering a wider area than sales. More in the guide.",
    cta: "Read how large the gap is and what it means",
  },
  features: {
    eyebrow: "Features",
    title: "What can we offer you?",
    description:
      "Everything you need for a safer property purchase decision — in one place.",
    items: [
      {
        title: "Official GURS data",
        description:
          "The same data the seller has — now on your side.",
      },
      {
        title: "Core market analysis",
        description:
          "See what properties on your street actually sold for.",
      },
      {
        title: "Location insights",
        description:
          "Find out what affects the value — before the buyer after you does.",
      },
      {
        title: "AI-powered valuation",
        description:
          "The computer connects 78+ data points that would take you weeks to review yourself.",
      },
      {
        title: "Negotiation PDF sheet",
        description:
          "A clear PDF with reasoned arguments for a lower price, and the concrete steps you must not skip before signing.",
      },
      {
        title: "Straightforward report",
        description:
          "A clear, well-structured PDF report, ready to save, share, or discuss with an expert.",
      },
    ],
  },
  howItWorks: {
    eyebrow: "Process",
    title: "How does it work?",
    description:
      "Enter an address, review the available information and decide what to do next.",
    steps: [
      {
        title: "Enter your details",
        description: "Enter the property address for analysis.",
      },
      {
        title: "AI analyses multiple sources",
        description:
          "Market prices, location, comparable sales, risks, and trends.",
      },
      {
        title: "Receive your report",
        description:
          "Value, warnings, and recommendations — easy to understand without expert knowledge.",
      },
      {
        title: "Negotiate with facts",
        description:
          "Concrete arguments for a lower price — backed by official data.",
      },
    ],
  },
  testimonials: {
    eyebrow: "Trusted by many",
    title: "What our users say",
    description:
      "Real experiences from agents, investors, and property owners across Slovenia.",
    items: [
      {
        name: "Marko",
        location: "Ljubljana",
        content:
          "Very happy with nReport. Their AI analysis matched actual sale prices closely. Great service!",
      },
      {
        name: "Ana",
        location: "Ljubljana",
        content:
          "As a licensed agent, I now use nReport for all my clients. The reports are indispensable — detailed, reliable, and professionally prepared. My buyers are always impressed.",
      },
      {
        name: "Matej",
        location: "Maribor",
        content:
          "When evaluating commercial spaces for expansion, nReport saved months of work. We got three valuations in one week, all backed by official GURS data. The investment paid off on day one.",
      },
      {
        name: "Petra",
        location: "Celje",
        content:
          "When dividing assets I need reliable valuations. nReport delivers exactly that — professional reports backed by official data.",
      },
      {
        name: "Gregor",
        location: "Koper",
        content:
          "I recommend nReport to clients for a quick informative valuation. Within 24 hours they get a professional report that helps them understand property value. Excellent service!",
      },
    ],
    prevAria: "Previous testimonial",
    nextAria: "Next testimonial",
    goToAria: "Go to testimonial",
  },
  pricing: {
    badge: "Choose a package",
    title: "Report packages",
    subtitle: "Choose the package that fits your search. Every paid package includes the full report.",
    subtitleHighlight:
      "1 credit = 1 report. Credits are valid for 1 year from purchase.",
    popular: "For comparing options",
    freePlan: {
      badge: "Free",
      name: "Free report preview",
      subtitle: "1 summary",
      priceLabel: "€0",
      specialNote: "After email verification",
      features: [
        "Official GURS data and assessed value",
        "Number of nearby sales and listings",
        "Open risks to check before signing",
      ],
      lockedNote:
        "Our value estimate and the rest of the report (market analysis, location, land registry, ETN sales) unlock with 1 credit",
      buttonText: "Try it free",
      buttonTextLoggedIn: "Go to dashboard",
    },
    addressWarning:
      'Address "{{address}}" does not include a house number. Please go back and enter a complete address with a house number to purchase credits.',
    anchorAppraisalLabel: "Court-appointed appraisal",
    anchorAppraisalValue: "€400 – €600",
    anchorOursLabel: "nReport informative report",
    anchorOursValue: "from €23.40",
    anchorNote:
      "The report does not replace a formal appraisal — it is a faster, cheaper basis for deciding whether the price is realistic and whether an appraisal is worth commissioning at all.",
    perReport: "/ report",
    includesGurs: "GURS extract and land registry (ZK) check included in all packages",
    notOfficialAppraisal: "An informative report for better decisions — it does not replace a formal appraisal",
    selectAddressFirst: "Select an address before purchasing",
    aptPrefix: "apt.",
    securePayment: "100% secure payment",
    securePaymentDesc: "via Stripe • No hidden fees",
    allCards: "💳 All cards",
    instantActivation: "⚡ Instant activation",
    toastAddressRequired: "Address required",
    toastAddressRequiredDesc:
      "Please select a property address before purchasing credits.",
    toastInvalidAddress: "Invalid address",
    toastInvalidAddressDesc:
      "The address must include a house number. Please enter a complete address.",
    toastAptRequired: "Apartment number required",
    toastAptRequiredDesc:
      "Please enter the apartment number before purchasing credits.",
    plans: [
      {
        name: "Package 1",
        subtitle: "Get 1 report",
        features: [
          "Official GURS data",
          "Market price comparison (completed sales + listings)",
          "Land registry status check",
          "AI value estimate",
          "PDF negotiation sheet with arguments for a lower price",
        ],
        buttonText: "Buy 1 credit",
      },
      {
        name: "Package 2",
        subtitle: "2 reports",
        specialNote: "Ideal for comparing multiple properties",
        features: [
          "PDF negotiation sheet with arguments for a lower price",
          "Everything in Package 1",
          "Save 20% per report",
        ],
        buttonText: "Buy 2 credits",
      },
      {
        name: "Package 3",
        subtitle: "5 reports",
        specialNote: "Lowest price per report",
        features: [
          "PDF negotiation sheet with arguments for a lower price",
          "Everything in Package 1",
          "Save 40% per report",
        ],
        buttonText: "Buy 5 credits",
      },
    ],
    buy: "Purchase",
  },
  faq: {
    eyebrow: "Help",
    title: "Frequently asked questions",
    description:
      "Answers to the most common questions about our property reports.",
    contactTitle: "Have a question?",
    contactDesc: "Write to us and we'll be happy to help.",
    contactCta: "Send a question",
    items: FAQ_EN,
  },
  ratingStats: {
    eyebrow: "Satisfaction",
    title: "Our users' satisfaction",
    description:
      "Real-time ratings and recommendations from users who have already received a report.",
    recommend: "of users recommend us",
    averageRating: "average rating",
    votes: "votes",
  },
  footer: {
    tagline:
      "Independent property reports for Slovenia. Clear facts, evidence and next steps.",
    legalHeading: "Legal information",
    terms: "Terms of service",
    privacy: "Privacy policy",
    cookies: "Cookies",
    dataDeletion: "Data deletion",
    cookieSettings: "Cookie settings",
    trustedSources: "Trusted sources",
    trustedSourcesDesc:
      "Public records and market data in one report. Availability of individual records and extracts depends on the property.",
    copyright: "© 2026 Noventis d.o.o. All rights reserved.",
    slogan: "nReport.ai — smarter property buying",
    backToTop: "Back to top",
  },
  newsletter: {
    heading: "nReport newsletter",
    description:
      "Property market analysis, new guides and product updates. Once a month, no spam.",
    placeholder: "you@email.com",
    submit: "Subscribe",
    success: "Thanks! You're subscribed.",
    error: "Subscription failed. Please try again.",
    invalidEmail: "Enter a valid email address.",
    privacy: "Unsubscribe at any time.",
    optIn: "Send me property market news (unsubscribe at any time).",
  },
  sampleExplorer: {
    loadError: "The tour could not load. You can still download the full sample.",
    loading: "Opening sample…",
    "title": "Explore a sample report",
    "description": "A quick tour of the estimate, evidence and open questions. This is a fictional example; the full sample PDF is in Slovenian.",
    "close": "Close sample",
    "sections": "Sample sections",
    "value": "Value",
    "evidence": "Evidence",
    "checks": "Checks",
    "valueTitle": "A price needs context.",
    "valueNote": "The estimate is a starting point for assessing an asking price. Its range shows uncertainty; a single figure cannot replace a viewing or a professional appraisal.",
    "salesNote": "Recorded sales show actual prices paid, dates and comparable properties.",
    "registryNote": "Area, construction year and property identifiers help establish what you are comparing.",
    "listingsNote": "Current listings show sellers’ expectations. Asking prices are not completed sale prices.",
    "askingPrices": "Asking prices",
    "availability": "Source coverage and comparable data depend on the property.",
    "checkTitle": "Know what still needs checking.",
    "checkNote": "Clearly marked open questions help you plan the next steps before signing. An available land-registry extract is the basis for checking ownership and encumbrances.",
    "openPdf": "Open full PDF",
    "back": "Back to overview",
    "price": "1 full report · {{price}}",
    "cta": "Choose a report"
  },
  pdfPreview: {
    title: "nReport sample report",
    download: "Download PDF",
    downloadShort: "Download",
    orderReport: "Order report",
    iframeTitle: "Sample PDF report",
  },
  contact: {
    eyebrow: "Contact",
    title: "Send us a question",
    description:
      "Fill in the form and we'll get back to you as soon as possible.",
    nameLabel: "Full name",
    namePlaceholder: "John Smith",
    emailLabel: "Email address",
    emailPlaceholder: "john@example.com",
    messageLabel: "Your question",
    messagePlaceholder: "Describe your question...",
    successTitle: "Message sent",
    successDesc:
      "Thank you for your question. We'll reply as soon as possible.",
    errorSend: "Could not send the message. Please try again.",
    validation: {
      nameRequired: "Name is required",
      nameTooLong: "Name is too long",
      emailInvalid: "Invalid email address",
      emailTooLong: "Email is too long",
      messageRequired: "Message is required",
      messageTooLong: "Message is too long",
    },
  },
  auth: {
    signedOutTitle: "Signed out",
    signedOutDesc: "You have been signed out.",
    signOutError: "Signing out failed. Please try again.",
    login: "Log in",
    register: "Sign up",
    loginDesc: "Log in to access your credits and reports.",
    registerDesc:
      "Create an account to purchase credits and access reports. Verify your email to get a free credit for a preview report.",
    displayName: "Display name",
    displayNamePlaceholder: "e.g. John Smith",
    email: "Email",
    password: "Password",
    showPassword: "Show password",
    hidePassword: "Hide password",
    legalNotice: "By registering you accept our",
    legalTerms: "Terms of Business",
    legalPrivacy: "Privacy Policy",
    passwordHint:
      "Password must be at least 8 characters and include uppercase, lowercase letters, and a number.",
    loginSubmit: "Log in",
    registerSubmit: "Sign up",
    loginWithGoogle: "Log in with Google",
    registerWithGoogle: "Sign up with Google",
    loginWithFacebook: "Log in with Facebook",
    registerWithFacebook: "Sign up with Facebook",
    noAccount: "Don't have an account? Sign up",
    hasAccount: "Already have an account? Log in",
    invalidEmail: "Enter a valid email address",
    forgotPassword: "Forgot password?",
    resetEnterEmail: "Enter your email address first.",
    resetSentTitle: "Reset link sent",
    resetSentDesc: "Check your inbox for a link to reset your password.",
    resetError: "Couldn't send the reset link. Please try again.",
    recoveryTitle: "Set a new password",
    recoveryDesc: "Choose a new password for your account.",
    recoverySubmit: "Update password",
    passwordUpdatedTitle: "Password updated",
    passwordUpdatedDesc: "You can now log in with your new password.",
    displayNameTooShort: "Display name must be at least 2 characters",
    passwordTooShort: "Password must be at least 8 characters",
    passwordNeedsLower: "Password must contain at least one lowercase letter",
    passwordNeedsUpper: "Password must contain at least one uppercase letter",
    passwordNeedsDigit: "Password must contain at least one number",
    invalidCredentials: "Invalid login credentials",
    resendConfirmationTitle: "Confirmation email sent",
    resendConfirmationSent: "Check your inbox for a new verification link.",
    resendConfirmationFailed: "Could not resend the verification email. Please try again in a few minutes.",
    userExists: "A user with this email already exists",
    weakPassword:
      "This password is too common and easy to guess. Please choose a stronger password.",
    registerSuccess: "Registration successful",
    registerSuccessDesc:
      "Check your inbox to confirm your email. You'll receive a free credit for a preview report after verification.",
    suspiciousActivity:
      "Unusual activity detected. Please wait a few minutes.",
    googleError: "Google sign-in failed. Please try again.",
    facebookError: "Facebook sign-in failed. Please try again.",
    redirectingToGoogle: "Redirecting to Google...",
    completingSignIn: "Completing sign-in...",
  },
  dashboard: {
    title: "Dashboard",
    loginPrompt: "Log in to access your dashboard.",
    loadingSession: "Completing sign-in...",
    welcome: "Welcome, {{name}}!",
    subtitle: "Your reports, credits, and orders — all in one place.",
    orderTitle: "Order a property report",
    orderDesc: "Enter the property address to prepare your report.",
    orderCta: "Order report (1 credit)",
    orderPaidCta: "Order detailed report (1 credit)",
    orderPreviewCta: "Get a free preview",
    orderPaidDesc: "Enter the property address. A detailed report uses 1 purchased credit.",
    orderPreviewDesc: "You have a free credit for a report preview. Unlocking the detailed report requires a purchased credit.",
    insufficientCredits: "Insufficient credits",
    insufficientCreditsDesc:
      "You don't have enough valid credits to order a report. 1 credit is required.",
    insufficientCreditsShort:
      "You don't have enough valid credits to order a report.",
    orderError: "Error ordering report",
    propertyNotFound: "Address not in cadastre",
    propertyNotFoundDesc:
      "The address was not found in the GURS registry. Check the house number and that the address is correct.",
    apartmentNotFound: "Apartment not found",
    apartmentNotFoundDesc:
      "No apartment with that unit number was found at this address in the cadastre.",
    orderSuccess: "Report ordered",
    orderSuccessDesc: "Your report is being prepared.",
    orderHeldDesc: "We add the land-registry extract and email you the report within 1 business day.",
    genericError: "Something went wrong. Please try again.",
    statCredits: "Available credits",
    statInProgress: "In progress",
    statCompleted: "Completed reports",
    statInProgressInline: "{{count}} in progress",
    statCompletedInline: "{{count}} completed",
    lowCreditsTitle: "You're out of credits",
    lowCreditsDesc:
      "Purchase credits below to order your next property report.",
    creditHistoryToggle: "Credit history",
    newReportEyebrow: "New report",
    reportsEyebrow: "Your reports",
    loadError: {
      retry: "Try again",
      unavailable: "Not loaded",
      hint: "Your data is not lost. Check your connection and try again.",
      credits: "We couldn't load your credit balance.",
      orders: "We couldn't load your reports.",
      transactions: "We couldn't load your credit history.",
    },
    expiringCredits: {
      title: "{count} credits expire soon",
      desc: "Your {count} unused credits expire on {date}. After that date they can no longer be used, so do put them to work in good time.",
    },
    bulk: {
      addAddress: "Add to list",
      eyebrow: "Several properties",
      title: "Order several reports at once",
      desc: "Add properties to the list and order them in one go. Each report uses one credit.",
      apartment: "Apartment no.",
      duplicate: "That property is already on the list.",
      remove: "Remove from list",
      placed: "Ordered",
      failed: "Failed",
      skippedNoCredits: "Not ordered — credits ran out.",
      needCredits: "{count} properties on the list · you have {balance} credits",
      partialWarning: "The first {n} will be ordered.",
      submit: "Order {count} reports",
      running: "Ordering {done}/{total} …",
      doneTitle: "Ordering finished",
      doneDesc: "Ordered {placed} of {total} reports.",
    },
  },
  checkout: {
    tagline: "Property reports",
    back: "Back",
    orderSummary: "Order summary",
    unlockTitle: "Report unlock",
    unlockDesc: "The credit will be spent on unlocking this report right after payment.",
    package: "Package",
    credits: "Credits",
    address: "Address",
    total: "Total",
    includesVat: "Includes VAT",
    vatExempt: "Final price. VAT is not charged (Article 94(1) of the Slovenian VAT Act, ZDDV-1).",
    securePaymentTitle: "Secure payment",
    securePaymentDesc:
      "Your payment details are handled by Stripe. Your data is safe and encrypted.",
    creditsValidity: "1 credit = 1 report. Credits are valid for 1 year from purchase.",
    paymentTitle: "Payment",
    paymentDesc: "Complete your purchase with secure payment via Stripe",
    loadingPayment: "Loading payment system...",
    legalTitle: "Legal notice",
    legalDesc:
      "The report is for informational purposes only and does not constitute an official property appraisal. Data and analyses are based on publicly available records and statistical models that do not replace a professional appraisal by a licensed valuer.",
    missingData: "Missing required purchase details. Please try again.",
    paymentEyebrow: "Payment",
    paymentSuccess: "Payment successful!",
    redirecting: "Redirecting...",
    creditOne: "credit",
    creditTwo: "credits",
    creditFew: "credits",
    creditMany: "credits",
  },
  paymentSuccess: {
    processingEyebrow: "Payment",
    processingTitle: "Confirming your payment…",
    processingDesc: "This only takes a few seconds. Please keep this page open.",
    loginTitle: "Log in to view payment status",
    loginDesc: "Your payment is safe. Log in with the account you paid with and we'll confirm your credits right here.",
    successEyebrow: "Payment confirmed",
    successTitle: "Thank you, payment received",
    successDesc: "Your credits are on your account and ready to use.",
    paymentFailed: "Payment was not successful",
    processingError: "An error occurred while processing your order.",
    stepsLabel: "Order progress",
    stepPayment: "Payment received",
    stepPaymentActive: "Confirming your payment…",
    stepCredits: "Credits added",
    stepCreditsActive: "Adding credits to your account…",
    stepCreditsDone: "{{credits}} added to your account",
    stepCreditsWaiting: "Taking longer than usual — the credits will be added automatically.",
    stepOrder: "Report started",
    stepOrderActive: "Starting the report for the address you entered…",
    stepOrderDone: "We've started the report for the address you entered. It'll appear in your dashboard shortly.",
    stepOrderFailed: "We couldn't start it automatically. Your credit is safe — order the report from your dashboard.",
    stepUnlock: "Full report",
    stepUnlockActive: "Unlocking your report…",
    stepUnlockDone: "The full report is ready to open.",
    stepUnlockHeld: "Paid. We deliver the full report, with the land-registry extract, within 1 business day.",
    stepUnlockFailed: "Your credit is safe — unlock the report with one click on its page.",
    stepNext: "Order your report",
    stepNextDesc: "Enter a property address on your dashboard. Each report uses 1 credit.",
    statusDone: "done",
    statusActive: "in progress",
    statusPending: "waiting",
    statusNext: "next step",
    statusAttention: "needs attention",
    delayedTitle: "Payment received, credits on their way",
    delayedDesc: "Your payment is confirmed, but adding the credits is taking longer than usual — it normally sorts itself out within a minute. Check again, or open your dashboard where the credits will appear.",
    checkAgain: "Check again",
    checkingAgain: "Checking…",
    errorTitle: "We couldn't confirm your payment yet",
    errorDesc: "If you were charged, your credits are safe. Try again in a moment or write to us.",
    missingSessionTitle: "We can't find this payment",
    missingSessionDesc: "The link is missing its payment reference. If you were charged, the credits will show up on your dashboard.",
    redirectDashboardIn: "Taking you to your dashboard in {{seconds}} s.",
    redirectReportIn: "Opening your report in {{seconds}} s.",
    stayHere: "Stay on this page",
    goToDashboard: "Go to dashboard",
    openReport: "Open the report",
    backHome: "Back to homepage",
    contactLead: "Questions? Write to us at",
    announceConfirming: "Confirming your payment.",
    announceCompleted: "Payment confirmed. {{credits}} added to your account.",
    guestTitle: "Payment received!",
    guestDesc: "We've emailed you a one-click link to log in and access your report credits.",
    guestSpam: "Don't see the email within a few minutes? Check your spam folder.",
  },
  postPurchaseSurvey: {
    question: "Quick question — what almost stopped you from buying?",
    options: {
      price: "The price",
      trust: "Unsure the data is accurate",
      unclear: "Unsure what's included",
      technical: "Payment/technical issues",
      nothing: "Nothing — it was easy",
    },
    skip: "Skip",
    thanks: "Thanks for your feedback!",
  },
  paymentTrust: {
    acceptedMethods: "We accept all major cards",
    securedByStripe: "Secured by Stripe",
    encryptedNote: "Your card details are encrypted and never touch our servers.",
  },
  checkoutSupport: {
    contact: "Contact support",
    cancellation: "14-day right of cancellation",
  },
  checkoutSteps: {
    package: "Package",
    payment: "Payment",
    done: "Confirmation",
  },
  checkoutUpsell: {
    title: "Buying more than one?",
    desc: "Get {{credits}} reports for €{{price}} — only €{{per}} each, {{discount}}% off per report.",
    cta: "Switch to this offer",
  },
  paymentCanceled: {
    eyebrow: "Canceled",
    title: "Payment canceled",
    desc: "Your payment was canceled. No funds were charged to your account.",
    help: "If an error occurred or you're having trouble paying, contact us for help.",
    backHome: "Back to homepage",
    tryAgain: "Try again",
    contact: "For payment help, contact us at info@nreport.ai",
  },
  notFound: {
    eyebrow: "Error 404",
    title: "Page not found",
    desc: "Sorry, the page you're looking for doesn't exist or has been moved.",
    backHome: "Back to homepage",
    goBack: "Go back",
  },
  errorBoundary: {
    eyebrow: "Error",
    title: "Something went wrong",
    desc: "An unexpected error occurred. Try reloading the page or contact support.",
    retry: "Try again",
    reload: "Reload page",
    devDetails: "Developer details",
  },
  quickPurchase: {
    title: "Quick credit purchase",
    popular: "Popular",
    packages: [
      { name: "Buy 1 credit", description: "Single report" },
      { name: "Buy 2 credits", description: "Save 20%" },
      { name: "Buy 5 credits", description: "Save 40%" },
    ],
  },
  orderStatus: {
    title: "My reports",
    desc: "Download completed reports or track ones in progress",
    noOrders: "No reports yet",
    noOrdersHint: "Order your first property report below.",
    defaultTitle: "Property report",
    apartment: "Apartment",
    house: "House",
    property: "Property",
    aptNo: "No.",
    downloadPdf: "Download PDF",
    openPdf: "Open report in new tab",
    refresh: "Refresh orders",
    loadMore: "Show older reports",
    loadingMore: "Loading older reports …",
    loadedCount: "{{count}} reports shown",
    loadMoreError: "Older reports could not be loaded. Please try again.",
    dateUnknown: "Date unknown",
    thumbnailAlt: "Map preview for {{address}}",
    readyBadge: "Ready",
    previewBadge: "Preview",
    unlockHeldBadge: "Paid – in preparation",
    hintCompleted: "Your report is ready to download.",
    hintProcessing: "We're analysing the property — usually takes a few minutes.",
    hintPending: "Your report is queued and will start shortly.",
    hintFailed: "Something went wrong. Contact support if this persists.",
    hintRefunded: "The report could not be started, so your credit has been returned. You can order it again.",
    hintNeedsReview: "Our automatic quality check found an inconsistency, so the report is being reviewed by hand before delivery. We will let you know when it is ready.",
    hintAwaitingApproval: "Your report is ready and we are giving it a final personal check before sending. You will receive it by email once it is approved.",
    statusUnknown: "Unknown",
    statusCompleted: "Completed",
    statusProcessing: "Processing",
    statusPending: "Pending",
    statusFailed: "Failed",
    statusCancelled: "Canceled",
    statusNeedsReview: "In review",
    viewReport: "View report",
    generation: {
      live: "Live",
      phaseQueued: "Queued",
      phaseChecking: "Checking the property",
      phaseWriting: "Writing the analysis",
      phaseMarket: "Market analysis and checks",
      phasePdf: "Building the PDF",
      phaseDone: "Ready",
      etaMinutes: "about {{count}} min left",
      etaUnderMinute: "less than a minute left",
      etaLonger: "Taking longer than usual — the report will still be delivered.",
      typicalDuration: "Usually takes about 3 minutes.",
      heldTitle: "Obtaining the land-registry extract",
      heldDesc: "A paid report includes the land-registry extract. We email you the finished report within 1 business day.",
      activeTitle: "Reports in progress",
      activeDesc: "Follow the progress live. A link to the report will appear here as soon as it is ready.",
      viewAll: "All reports",
      duplicateTitle: "You already have a report for this address",
      duplicateDesc: "An order for {{address}} already exists ({{status}}). A new order spends another credit and creates a second report for the same property.",
      duplicateConfirm: "Order anyway (1 credit)",
      duplicateCancel: "Cancel",
    },
  },
  reportDetail: {
    navigationTitle: "Explore this report",
    loading: "Loading report...",
    notFound: "Report not found.",
    dataUnavailable: "Report data is not available yet.",
    backToDashboard: "Back to dashboard",
    paywall: {
      eyebrow: "Free preview",
      title: "Just one step to your full report",
      description: "We have already gathered and analysed the data for {{address}}. Here is what unlocking gives you.",
      findingsTitle: "What we found for this property",
      estimateLabel: "Estimated market value",
      estimateNote: "with a range and confidence level",
      ceilingLabel: "Rounded reference value",
      ceilingNote: "a rounded analytical starting point to assess against your circumstances",
      salesLabel: "Completed sales nearby",
      salesYears: "{{from}}–{{to}}",
      salesClosest: "closest {{distance}} m away",
      listingsLabel: "Comparable listings",
      listingsNote: "average asking price per m²",
      gapLabel: "Asking vs. achieved prices",
      gapNote: "gap between listing prices and sale prices nearby",
      argumentsLabel: "Negotiating arguments",
      argumentsNote: "with a printable sheet for the viewing",
      riskLabel: "Check before you sign",
      riskNote: "The full report says exactly what to do.",
      includesTitle: "The full report includes",
      includes: {
        estimate: "Indicative value estimate with a range and confidence level",
        method: "Step-by-step calculation from official data",
        sales: "Completed sales (ETN) on a map with prices and dates",
        trend: "Price trend chart for the area by year",
        listings: "Comparable listings with prices and links",
        location: "Location analysis and nearby services",
        cadaster: "Key building features and cadastral data",
        risks: "Risk list and steps to take before signing",
        negotiation: "Negotiating points and a printable viewing sheet",
        attachments: "Appendices: GURS valuation and cadastral excerpt",
        attachmentsEnergy: "Appendices: GURS valuation, cadastral excerpt and energy certificate",
        pdf: "Full PDF to download and print",
      },
      priceNote: "One-off payment · no subscription",
      buyCta: "Unlock the full report",
      instant: "We deliver the full report, with the land-registry extract, within 1 business day.",
      heldTitle: "Paid – we are preparing your full report",
      secure: "Secure card payment via Stripe",
      sample: "See a sample full report (PDF)",
      bundle: "Buying more than one property? 5 reports for {{price}} ({{per}} per report)",
      stickyLabel: "Free preview",
      stickyCta: "Unlock for {{price}}",
      unlockCta: "Unlock report (1 credit)",
      unlocking: "Unlocking...",
      creditsAvailable: "You have {{count}} purchased credits available",
      unlockedTitle: "Report unlocked",
      unlockedDesc: "The full report is now available.",
      unlockedPdfPending: "Report unlocked. The full PDF is still being prepared — try the download again in a moment.",
      unlockFailedTitle: "Unlock failed",
      downloadPreview: "Download preview",
    },
    attachments: {
      title: "Attachments",
      description:
        "Source documents attached to this report (also included in the downloaded PDF).",
      valuation: "Property valuation (GURS)",
      cadastral: "Cadastral excerpt (GURS)",
      energy: "Energy certificate",
      uploaded: "Uploaded document",
    },
    badge: "nReport.ai property report",
    title: "Property analysis",
    sectionOverviewEyebrow: "Property overview",
    sectionOverviewTitle: "Key characteristics",
    sectionOverviewDesc:
      "Type and use of the property, its position in the building, layout, construction and equipment.",
    sectionFeaturesEyebrow: "Summary",
    sectionFeaturesTitle: "Key data points",
    sectionMarketEyebrow: "AI market analysis",
    sectionMarketTitle: "Advanced market analysis",
    sectionMarketDesc:
      "Comprehensive market analysis using AI and statistical models.",
    sectionMethodologyEyebrow: "Methodology",
    sectionMethodologyTitle: "Informative value",
    sectionMethodologyDesc:
      "A weighted estimate that reconciles official GURS value with listing and realized market prices.",
    sectionLocationEyebrow: "Location",
    sectionLocationTitle: "Location in context",
    sectionLocationDesc: "Analysis of the property location and surroundings.",
    sectionCadasterEyebrow: "Land registry",
    sectionCadasterTitle: "Land registry extract analysis",
    sectionValuationEyebrow: "Property valuation",
    sectionValuationTitle: "Official property value",
    sectionValuationDesc: "GURS generalized market value of building and land.",
    sectionEtnEyebrow: "Transaction history",
    sectionEtnTitle: "Completed deals nearby",
    sectionEtnDesc:
      "Overview of completed sales of comparable properties from the ETN register.",
    sectionAdsEyebrow: "Market overview",
    sectionAdsTitle: "Comparable listings",
    sectionAdsDesc: "Selected listings with the most comparable metrics.",
    sectionAdsDescCohort:
      "A representative {{shown}} of the {{total}} listings in the asking-price analysis. The average, range and confidence use all {{total}}.",
    adsScopeTitle: "How the comparable listings were chosen",
    recommendations: "Recommendations",
    priceAnalysis: "Price analysis",
    marketPosition: "Market position",
    propertyCharacteristics: "Property characteristics",
    investmentInsights: "Investment insights",
    buildingValuation: "Building valuation",
    landValuation: "Land valuation",
    satelliteMap: "Satellite view",
    streetView: "Street View",
    disclaimerTitle: "Legal notice",
    disclaimerText:
      "This report is for informational purposes only and does not constitute an official property appraisal. Data and analyses are based on publicly available records and statistical models that do not replace a licensed appraiser.",
    area: "Area",
    netArea: "Net floor area",
    usableArea: "Usable area",
    landArea: "Land size",
    yearBuilt: "Year built",
    metricInformativeValue: "Informative value",
    metricGursValue: "GURS generalised value",
    metricMarketPriceM2: "Market estimate (listings)",
    metricEtnPriceM2: "Realized value (ETN)",
    metricEtnGrowth: "Annual price growth (CAGR)",
    metricEtnGrowthHint: "Historical growth of realised prices per m² nearby ({{period}}). Not a rental yield and not a forecast.",
    metricEtnHistoricalHint:
      "Average of older sales — no comparable sale in the recent window, so it is not part of the estimate.",
    metricEtnGrowthHintNoPeriod:
      "Historical growth of realised prices per m² nearby. Not a rental yield and not a forecast.",
    gursGapLabel: "Gap vs GURS",
    distributionTitle: "Estimate beside recorded contract prices",
    distributionLead:
      "The chart shows contract prices per m² and the informative estimate. These contract prices have not been adjusted to the estimate’s date or model basis; the difference alone does not establish a discount or overpricing.",
    distributionAlt: "Distribution of realised prices per m²",
    negotiationSheetDownload: "Negotiating sheet",
    negotiationEyebrow: "Negotiating brief",
    negotiationTitle: "Arguments backed by data",
    negotiationDesc:
      "Every argument below comes from data in this report and states its source. They are meant to be said out loud, not explained.",
    negotiationCeilingLabel: "Rounded reference value",
    negotiationBatna:
      "Set your personal maximum offer using your budget, purchase and repair costs, and unresolved questions. It may differ from this analysis's reference value.",
    negotiationSourcePrefix: "Source:",
    negotiationHowToTitle: "How to use these arguments",
    negotiationHowTo: [
      "Do not open with a price. Ask: \"How did you arrive at this price?\" Let the seller explain, then show the data.",
      "Show, do not demand: \"I looked at realised sales nearby — comparable properties sold for X. How do you read the difference?\"",
      "Do not apologise for your offer. It is backed by data; the document is your argument, not you.",
      "Once you say a number, stop talking. Silence is a negotiating tool.",
      "If you hear \"that is below market\", ask: \"Which realised sales support that?\"",
    ],
    negotiationLimitsTitle: "Limitations.",
    negotiationLimits:
      "This analysis is based on public records (GURS, ETN, listing platforms) and does not replace a formal appraisal. Realised sales are the best available proxy for market value, not absolute truth. The outcome also depends on the seller's motivation, financing terms and factors this analysis cannot anticipate.",
    keyFactsEyebrow: "Property basics",
    keyFactsTitle: "The property at a glance",
    addressSpreadTitle: "Range of recorded prices at the same address",
    addressSpreadAnd: "and",
    addressSpreadExplain:
      "Sales at the same address can concern different building units, sale dates and contract terms. The price range alone establishes neither comparability nor a data error. The register cannot establish the full cause of the difference; consider the stated facts and limitations of each sale.",
    selectionNoteTitle: "How the comparable sales were chosen.",
    selectionNote:
      "Within a {{radius}} km radius we reviewed {{considered}} realised sales and used {{used}} of them. We kept sales with a floor area between {{min}} and {{max}} m².",
    selectionNoteNoSize:
      "Within a {{radius}} km radius we reviewed {{considered}} realised sales and used {{used}} of them. This property's floor area is not recorded in GURS, so sales were not filtered by size and size comparability is not guaranteed.",
    selectionNoteLandRatio: "For houses we also required a similar land-to-building ratio.",
    selectionNoteOutliers:
      "{{count}} sales were excluded as statistical outliers — their price per m² was so far outside the local range that a non-market transfer is the likeliest explanation.",
    selectionNoteCohort:
      "The estimate uses {{used}} of the {{considered}} sales reviewed within {{radius}} km — those completed in the {{months}} months before the valuation date ({{date}}).",
    selectionNoteCohortNone:
      "None of the {{considered}} sales reviewed within {{radius}} km is a comparable completed in the {{months}} months before the valuation date ({{date}}), so realised sales are not part of the estimate.",
    selectionNoteSizeBand:
      "We kept sales with a floor area between {{min}} and {{max}} m².",
    selectionNoteNoSizeShort:
      "This property's floor area is not recorded in GURS, so sales were not selected by size.",
    selectionNoteHistorical:
      "{{count}} older comparable sales are shown as historical context only (marked as older); they are not part of the estimate.",
    selectionNoteExcluded:
      "Excluded: {{count}}",
    selectionExclIneligible:
      "{{count}} not eligible as comparables (e.g. share sales, related-party transfers)",
    selectionExclNotComparable:
      "{{count}} by size, land-to-building ratio or price",
    selectionExclOutliers:
      "{{count}} statistical outliers",
    selectionExclDate:
      "{{count}} without a valid date or duplicated",
    evidence: {
      eyebrow: "Evidence for the estimate",
      title: "The strongest comparable sales and the path to the estimate",
      desc: "Which realised sales are most similar to this property, how they differ, and how the observed prices become the estimate.",
      headlinePriced: "Showing the {{shown}} most similar of the {{priced}} sales in the estimate; all of them set the estimate, not only those shown.",
      headlineThin: "Only {{priced}} realised sale(s) from the last 24 months are in the estimate: the evidence is thin.",
      headlineHistorical: "No comparable realised sale in the last 24 months; the older sales below are historical context only.",
      headlineNoSales: "No comparable realised sale nearby.",
      headlineNoArea: "We did not calculate an informative value because the property's floor area is not recorded in GURS.",
      headlineNoRecent: "We did not calculate an informative value: there is no comparable realised sale in the last 24 months and no comparable listing.",
      headlineWithheld: "We did not publish an informative value: comparable sales are too few or too dispersed for a reliable estimate.",
      headlineContextSuffix: "The older sales below are historical context only.",
      colSale: "Sale",
      colDate: "Date",
      colDistance: "Distance",
      colArea: "Area",
      colPrice: "Price",
      colPricePerM2: "Price/m²",
      colSimilarity: "Similarity",
      sourceId: "ETN transaction ID {{id}}",
      typeApartment: "apartment",
      typeHouse: "house",
      addressUnknown: "Address not published",
      atValuationDate: "at the valuation date {{price}}",
      contextMarker: "older sale — context only",
      yearOnly: "year {{year}}",
      knownDifferences: "Known differences",
      dealNotes: "Sale notes",
      areaBasis: "Area basis",
      areaBasisDwelling: "sold net floor area of the dwelling",
      areaBasisAllParts: "sold area of all parts in the transaction",
      diffSizeSimilar: "similar size ({{area}})",
      diffSizeLarger: "{{pct}} larger ({{area}})",
      diffSizeSmaller: "{{pct}} smaller ({{area}})",
      diffSizeUnknownSubject: "{{area}}; this property's area is unknown",
      diffYearSame: "built at the same time ({{year}})",
      diffYearNewer: "{{years}} years newer ({{year}})",
      diffYearOlder: "{{years}} years older ({{year}})",
      diffYearUnknown: "build year unknown",
      diffYearUnknownSubject: "built {{year}}; this property's build year is unknown",
      diffLandSimilar: "similar land ({{area}})",
      diffLandMore: "{{pct}} more land ({{area}})",
      diffLandLess: "{{pct}} less land ({{area}})",
      diffLandUnknownSubject: "land {{area}}; this property's land is unknown",
      diffLandNone: "no land recorded in the transaction",
      flags: {
        ancillary_included: "price includes a garage, parking space or storage (not in the m²)",
        outbuildings_included: "price includes ancillary or farm buildings",
        minor_extra_land: "sale includes a small extra plot",
        shared_land: "land sold as a co-ownership share",
        vat_included: "price includes VAT",
        vat_rate_mixed: "price includes VAT at mixed rates",
        new_build: "new build",
        voluntary_auction: "sold at a voluntary auction",
        gurs_market_data_issue: "GURS: market sale with incomplete data",
        gurs_unverified: "not yet verified by GURS",
      },
      priceAdjustment: "Price adjustment",
      adjSize: "size {{pct}}",
      adjAge: "build year {{pct}}",
      adjTotal: "total {{pct}}",
      adjNone: "no adjustment (size and build year match or are unknown)",
      setTitle: "Sales shown and the full analysis.",
      setWeighting: "Every sale from the last {{months}} months ({{priced}}) enters the ETN average, not only those shown, weighted by floor area (sum of prices / sum of m²).",
      setWeightsAsking: "Realised sales carry {{etn}} % of the estimate, asking prices ({{listings}} listings) the rest.",
      setWeightsOnly: "Realised sales carry {{etn}} % of the estimate.",
      setEtnZero: "Realised sales carry no weight in this estimate.",
      setNone: "No realised sale entered the estimate.",
      setAverages: "The sales shown average {{shown}}; all sales in the estimate ({{priced}}) average {{all}}.",
      setExcluded: "{{excluded}} of the {{considered}} sales reviewed were excluded; the reasons are listed with the sale selection.",
      similarityMethod: "Similarity (0–100) combines distance, the difference in net floor area, the difference in build year, the age of the sale and, for houses, the difference in land size. A missing figure counts as a half mismatch. Property type matches for every sale because only sales of the same type are selected.",
      bridgeTitle: "From observed prices to the estimate",
      legs: {
        etn: "Realised sales (ETN)",
        market: "Asking prices",
        gurs: "GURS generalised value",
      },
      weight: "weight {{pct}} %",
      observedEtn: "Observed: sum of prices / sum of m² of all {{n}} sales in the estimate",
      observedMarket: "Observed: average asking price of {{n}} listings in the analysis",
      observedGurs: "Observed: GURS generalised value / valuation area",
      steps: {
        etn_time_index: "Time adjustment to the valuation date {{pct}} (measured: local annual price change fitted on the same sales)",
        etn_uplift: "ETN price uplift {{pct}} (model assumption, not independently verified)",
        asking_haircut_measured: "Asking-price reduction {{pct}} (measured asking/realised ratio; likely an upper bound)",
        asking_haircut_assumed: "Asking-price reduction {{pct}} (assumption, smaller than the measured gap)",
        gurs_uplift: "GURS generalised-value uplift {{pct}} (model assumption, not independently verified)",
        etn_comparable_adjustments: "Adjustment of the comparable sales for differences in size and build year {{pct}} (measured on recorded sales; each sale shows its own adjustment)",
        subject_position: "Floor and lift {{pct}} (official GURS position factor for this flat; the comparable sales' floors are not recorded)",
      },
      statusMeasured: "measured",
      statusAssumed: "assumption",
      effectOnEstimate: "Effect on the estimate: {{eur}}",
      contribution: "Contribution to the estimate: {{price}} × {{share}}",
      downweight: "The weight of realised sales is multiplied by {{value}} because adjusted asking prices are much higher (model assumption, not independently verified).",
      weightedPrice: "Weighted price per m²",
      roundedFrom: "rounded from {{value}}",
      timesArea: "× {{label}} {{area}} = informative value",
      noSubjectAdjustments: "The estimate makes no adjustment for differences between the comparable sales and this property (size, age, land, condition, energy class, floor): we have no verified local coefficient for any of them. These differences are listed, not priced.",
      subjectAdjustmentsSizeAge: "Differences in size and build year are priced with coefficients measured on recorded sales (smaller flats achieve a higher price per m²); each sale shown lists its own adjustment.",
      subjectAdjustmentsPosition: "Floor and lift are taken into account with the official GURS position factor for this flat.",
      subjectAdjustmentsRest: "Land, condition and energy class are not priced because we have no measured coefficient for them.",
      subjectAdjustmentsRestNoSizeAge: "Size, age, land, condition and energy class are not priced because we have no measured local coefficient for them; the differences are listed with each sale.",
      rangeNote: "The range {{min}} – {{max}} is explained in the methodology.",
      mismatch: "Mismatch:",
      driversTitle: "What moves the estimate most",
      driverSource: "{{label}}: {{pct}} % of the estimate — {{price}} after adjustments, contribution {{eur}}",
      driverStep: "{{step}}: {{eur}} on the estimate",
      unpricedTitle: "What the estimate does not price",
      unpricedTitleNoEstimate: "What the comparison with sales does not capture",
      unpriced: {
        condition: "Condition and renovations: no data for this property or the comparable sales, so the difference is not priced. A renovated property, or one that needs renovation, can differ from the estimate substantially.",
        energy: "Energy class: the transaction register has no data for the comparable sales, so the difference is not priced.",
        floor: "Floor and lift: not in the transaction register, so the difference is not priced.",
        floor_position: "Floor and lift: the transaction register has no data for the comparable sales; for this property the official GURS position factor is applied, but the difference to each individual sale is not priced.",
        land: "Land: differences in land size and use are shown per sale but not priced.",
        land_unknown: "Land: this property's land area is unknown, so land cannot be compared.",
        package_contents: "Transaction contents: for some sales the price includes a garage, parking space or outbuildings whose area is not in the m²; their value is not deducted.",
      },
      nextStepsTitle: "What to check next",
      nextSteps: {
        get_area: "Obtain the unit's net floor area (GURS building cadastre, building plans or the seller); without it no estimate can be calculated.",
        appraisal_no_recent_sales: "For a current value, commission a certified appraisal: there is no comparable realised sale in the last 24 months, and older prices are not today's value.",
        appraisal_withheld: "For a reliable value, commission a certified appraisal: comparable realised sales are too few or too dispersed.",
        thin_evidence: "The estimate rests on very few realised sales; one atypical sale can move it a lot. Get more comparisons or an appraisal before a binding offer.",
        no_recent_sales: "No comparable realised sale in the last 24 months; for today's price level rely on an appraisal or verified agreed prices nearby.",
        inspect_condition: "Check condition, renovations and energy class at the viewing and in the energy certificate — these are the main differences the estimate does not price.",
        size_spread: "Most sales shown differ in floor area by 20 % or more; price per m² is usually higher for smaller units and lower for larger ones.",
        house_parcels: "Check which parcels and outbuildings are included in the sale (land register, cadastral map) and compare them with the sales above.",
        etn_unweighted: "Realised sales carry no weight in this estimate (it rests on asking prices); ask the seller or agent for achieved prices of comparable properties.",
      },
    },
    actionEyebrow: "Next steps",
    actionTitle: "What to check before you sign",
    actionDesc:
      "The order is deliberate: first what we could not verify for this property, then the standard checks that apply to any purchase.",
    limitationsTitle: "Limitations of this report",
    limitationsIntro: "Some information could not be obtained or verified when this report was produced. Below is what is missing, why, and what you can do. The remaining information is taken from the cited public registers and listings as they stood when the report was produced; errors in the registers themselves cannot be ruled out.",
    limitationsRecovery: "What you can do:",
    qualityReviewNotice: "This report is under review because the automatic quality check could not confirm all of its figures. Please do not rely on the numbers until the review is complete.",
    execTitle: "Executive summary",
    cadastralIdentity: { building: "Building", apartment: "Apartment", part: "Cadastral unit", parcel: "Parcel", municipality: "Cadastral district" },
    scopeWholeTitle: "Valuation scope: the whole building",
    scopePartTitle: "Valuation scope: an individual building unit",
    scopeParts: "Recorded units included",
    scopeAreaParts: "Units contributing to the valuation area",
    scopeWholeNote: "If you are buying only one part of the house, do not use the whole-building estimate for it. Confirm which units are included in the sale.",
    scopePartNote: "This is an analysis of the stated unit, not the whole building. Confirm that it is the unit you are buying.",
    execValue: "Indicative value estimate",
    execGursOnlyValue: "GURS-based orientation",
    execRangePrefix: "Indicative model range",
    execRangeNote: "The range does not guarantee the eventual sale price will fall within it and is not a negotiation range.",
    execInterpretations: {
      high: "Comparable completed sales support the estimate. It does not establish a maximum sensible offer; your decision also depends on actual condition, costs and your budget.",
      medium: "The estimate uses the available sources with the stated limitations. Before a binding offer, check comparability, actual condition and costs; this amount is not an offer ceiling.",
      low: "The estimate is primarily indicative. We recommend further verification before a binding offer.",
      gurs_only: "The amount comes only from GURS with an assumed model adjustment. There are no usable comparable sales or listings, so it is for orientation only. Obtain an independent appraisal before a binding offer.",
      limits: "Reasons for limited confidence:",
    },
    execCeiling: "Rounded reference value",
    execCeilingNote: "Rounded starting point from this analysis. The estimate range expresses uncertainty, not a negotiation range.",
    execTopRisk: "Main risk",
    execNoRisk: "No open issues",
    execDisclaimer:
      "An informative report for better decisions — it does not replace a formal appraisal.",
    riskEyebrow: "Verification status",
    propertyChecks: propertyChecksEn,
    supportingDocuments: supportingDocumentsEn,
    riskTitle: "What was checked, and what was not",
    riskDesc:
      "This analysis is based on public records. Below we state explicitly what we could verify, what we could not, and what is worth checking before you sign.",
    appraisalTitle: "Before a binding offer",
    appraisalBeforeOffer: "before the offer",
    confidenceTitle: "Confidence in the estimate",
    confidenceHigh: "High",
    confidenceMedium: "Moderate",
    confidenceLow: "Low",
    confidenceHighNote:
      "There are enough comparable realised sales and listings nearby to support the estimate firmly. A buyer has a reliable basis for negotiating, a seller for setting a price.",
    confidenceMediumNote:
      "The estimate uses the available sources but there is room for deviation. Treat it as orientation rather than an exact figure, and allow for a wider range when negotiating.",
    confidenceLowNote:
      "The estimate has limitations in its property or market evidence and is indicative. The reasons and supporting data are listed below; further checks or an independent appraisal are recommended before an important decision.",
    confidenceLimits: {
      few_comparable_sales: "Few comparable realised sales",
      wide_comparable_spread: "Wide spread of comparable sale prices",
      very_wide_comparable_spread: "Very wide spread of comparable sale prices",
      stale_comparable_sales: "Older comparable sales",
      very_stale_comparable_sales: "Very old comparable sales",
      sources_disagree: "Price sources disagree",
      sources_disagree_strongly: "Price sources disagree strongly",
      house_segment: "Houses vary more in condition, land and age",
      subject_area_unconfirmed: "Area is not confirmed across the available sources",
      subject_data_limited: "Limited subject or valuation-area data",
      asking_evidence_weak: "Wider listing area or limited comparability",
      gurs_only: "Estimate derives only from the GURS model reference",
    },
    confidenceComparables: "Comparable realised sales",
    confidenceListings: "Comparable active listings",
    confidenceListingQuality: "Listing evidence quality",
    confidenceDispersion: "Price spread across comparable sales",
    confidenceDataAge: "Age of the most recent sale",
    confidenceMonths: "{{count}} mo.",
    avgPrice: "Average price",
    medianPrice: "Median price",
    minPrice: "Lowest price",
    maxPrice: "Highest price",
    pricePerM2: "Price per m²",
    propertyValue: "Property value",
    marketAverage: "Asking prices, scaled to this floor area",
    askingHaircut: "Discount from asking price applied",
    differenceFromAsking: "Difference from asking prices per m²",
    pricePercentile: "Price percentile",
    marketSegment: "Price group among listings",
    positionValueLabel: "Compared calculation",
    marketSegmentLabels: {"budget": "Lower quarter", "average": "25th to 50th percentile", "premium": "50th to 75th percentile", "luxury": "Upper quarter"},
    avgSize: "Average size",
    avgYearBuilt: "Average year built",
    newerProperties: "Newer properties",
    olderProperties: "Older properties",
    askingVsSold: "Asking vs sold delta",
    negotiationPotential: "Negotiation potential",
    negotiationNotAssessed: "Not assessed. The gap between asking and sale prices does not determine a discount for this property.",
    marketLiquidity: "Market liquidity",
    gursGeneralized: "GURS generalized value",
    landGursValue: "GURS land value",
    analyzedDeals: "Analyzed deals",
    avgEtnPrice: "Average price",
    priceTrend: "Price trend",
    closestDeal: "Closest deal",
    year: "Year",
    transactions: "Transactions",
    avgPriceM2: "Avg. price/m²",
    medianPriceM2: "Median price/m²",
    change: "Change",
    overallGrowth: "Overall growth",
    annualizedGrowth: "Annual growth (CAGR)",
    comparableArea: "Comparable area (net, ancillary space weighted)",
    comparableAreaShort: "Comparable area",
    valuedOnArea: "{{price}} × comparable area {{area}}",
    comparableAreaFlat: "Comparable area = (usable {{usable}} + {{weight}} × ancillary space {{ancillary}}) / {{ratio}}{{unallocated}} = {{area}} (net floor area {{net}}).",
    comparableAreaUnallocated: " + unallocated space {{area}}",
    comparableAreaHouse: "Comparable area = net {{net}} − ancillary space {{ancillary}} + {{weight}} × {{ancillary}} = {{area}}: terraces and cellars count with a lower weight than the average m² of comparable houses.",
    comparableAreaHouseNoAncillary: "The comparable area equals the net floor area {{net}} (GURS lists no terraces or cellars).",
    comparableAreaWhy: "Comparable sales are priced per m² of net floor area, which includes their own cellars and balconies, so ancillary space is weighted the same way on both sides.",
    growthCaveatNote: "‡ A change of more than 25 % in one year ({{years}}) most likely reflects a different mix of properties sold in this local sample (size, age, new builds), not an actual price movement.",
    anomalyNote:
      "* A year with very few deals and a price far off the local average — usually a single atypical transaction (a share sale, a related-party transfer, or a property of a different category). We still show it, but leave it out of the growth figures.",
    address: "Address",
    price: "Price",
    distance: "Distance",
    dealDate: "Deal date",
    indexedPricePerM2: "At valuation date",
    indexedPriceNote:
      "At valuation date {{date}}: the realised price restated at that date using the estimated local annual change of {{rate}}. Older raw sale prices cannot be compared directly with asking prices from the report period. Sales more than five years old at the valuation date are not indexed. † marks an older sale excluded from the estimate.",
    adDescription: "Description",
    adPriceLevel: "Price level",
    adPriceBelow: "Below local asking",
    adPriceWithin: "In line with asking",
    adPriceAbove: "Above local asking",
    adLocation: "Location",
    link: "Link",
    openAd: "Open listing",
    na: "N/A",
    paginationPrevious: "Previous",
    paginationNext: "Next",
    paginationShowing: "Showing {{from}}–{{to}} of {{total}}",
    filtersTitle: "Filters",
    filterMin: "Min",
    filterMax: "Max",
    filtersClear: "Clear",
    mapSubject: "Subject property",
    mapComparablesTitle: "Comparable sales on the map",
    share: {
      button: "Share",
      title: "Share report",
      description: "Anyone with this link can view this report. No login required.",
      notCompleted: "You can share the report once it is ready.",
      generate: "Create link",
      generating: "Creating…",
      linkLabel: "Public link",
      copy: "Copy",
      copied: "Copied!",
      revoke: "Revoke access",
      revoking: "Revoking…",
      notShared: "This report isn't shared yet.",
      error: "Something went wrong. Please try again.",
    },
    sharedCta: "Create your own report",
    sharedClosingTitle: "Check another property",
    sharedClosingText:
      "Someone prepared this report for one specific address. You can run the same check on any address in Slovenia — the first report is free.",
    sharedClosingCta: "Check your property",
    sharedUnavailableTitle: "Report unavailable",
    sharedUnavailableText: "This report link is no longer available.",
    intel: {
      eyebrow: "Local listing market",
      title: "Neighbourhood market intelligence",
      desc: "Analysis of active listings for comparable properties nearby (nepremicnine.net).",
      sampleNote: "Based on {{count}} active listings ({{locality}}).",
      sampleNoteAsOf: "Based on {{count}} active listings ({{locality}}), as of the last capture on {{date}}.",
      medianAsking: "Median asking price",
      askingRange: "Range (P25–P75)",
      yourValue: "Your estimated value",
      percentile: "Position among listings",
      pricingBelow: "Your value per m² is below most local asking prices.",
      pricingInline: "Your value is in line with local asking prices.",
      pricingAbove: "Your value per m² is above most local asking prices.",
      newBuildShare: "New-build share",
      priceDrops: "Asking-price reductions",
      dropsDetail: "{{share}} of listings, avg {{avg}}",
      observedAge: "Observed listing age (median)",
      observedAgeValue: "{{days}} days",
      observedAgeLowerBound: "at least {{days}} days",
      delisted: "Withdrawn listings (last 30 days observed)",
      delistedDetail: "{{delisted}} withdrawn / {{active}} active",
      activityOutage: "Listing capture has been interrupted since {{date}}, so listing age and withdrawals are not shown; asking prices reflect the last capture.",
      activityShallow: "The listing archive does not yet have a long enough uninterrupted history, so listing age and withdrawals are not shown.",
      activityNote: "Observed age is the time between our first and last capture of a listing, not its original publication date. A withdrawn listing is not a sale. We do not estimate selling time, because listings cannot be linked to recorded sales.",
    },
  },
  creditHistory: {
    title: "Credit history",
    filterPlaceholder: "Filter by type",
    filterAll: "All transactions",
    filterPurchase: "Purchases",
    filterUsage: "Usage",
    filterExpiring: "Expiring soon",
    date: "Date",
    noResults: "No results.",
    noResultsHint: "Try different filters.",
    noTransactions: "No transactions.",
    noTransactionsHint: "Your transactions will appear here.",
    expired: "Expired",
    expiresIn: "Expires in {{days}} days",
    validUntil: "Valid until",
  },
  creditBalance: {
    title: "My credits",
    available: "Available credits",
    refresh: "Refresh balance",
  },
  embeddedCheckout: {
    loginRequired: "You must log in to pay.",
    createError: "Error creating payment. Please try again.",
    preparing: "Preparing payment...",
    tryAgain: "Try again",
    successTitle: "Payment successful!",
    successDesc: "Credits have been added to your account.",
  },
  checkoutModal: {
    eyebrow: "Payment",
    purchase: "Purchase: {{name}}",
    purchaseCredits: "Purchase {{count}} credits",
  },
  ratePage: {
    backHome: "Back to homepage",
  },
  userRating: {
    title: "How would you rate our service?",
    subtitle: "Your feedback helps us improve",
    rateAria: "Rate {{star}} out of 5",
    saveError: "Could not save your rating.",
    thanksToast: "Thank you!",
    thanksToastDesc: "Your rating was saved successfully.",
    thanks: "Thanks for your rating!",
  },
  cookieConsent: {
    title: "We respect your privacy",
    description:
      "We use strictly necessary cookies to run the website. Analytics and marketing cookies are only loaded with your consent. You can change your choice at any time.",
    cookiePolicyLink: "Cookie Policy",
    privacyPolicyLink: "Privacy Policy",
    necessaryTitle: "Strictly necessary",
    necessaryDesc: "Sign-in, security and payment. These cannot be switched off.",
    analyticsTitle: "Analytics",
    analyticsDesc: "Measuring traffic and usage so we can improve the service.",
    marketingTitle: "Marketing",
    marketingDesc: "Measuring ad performance and showing more relevant ads.",
    acceptAll: "Accept all",
    rejectAll: "Reject non-essential",
    customize: "Settings",
    saveSelection: "Save selection",
  },
  withdrawal: {
    heading: "Before payment: immediate performance",
    explainer:
      "A report is digital content we produce immediately after payment. Slovenian consumer law (Article 135 ZVPot-1) requires you to expressly authorise us to start immediately and to acknowledge that you thereby lose the 14-day right of withdrawal for the report produced. Unused credits remain refundable for 14 days.",
    consentLabel:
      "I agree that performance begins immediately and acknowledge that on full performance I lose my right of withdrawal for the report produced.",
    termsPrefix: "By continuing you accept:",
  },
  legal: legalEn,
};
